import Loading from "@/components/loading/loading.vue";
import { Annotation, ImageMetaData } from "@/models/image-meta-data";
import { fabric } from "fabric";
import { IObjectOptions } from "fabric/fabric-impl";
import { debounce } from "lodash";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { AnnotationTypes } from "../annotations/objects/annotation-type";
import { Circle } from "../annotations/objects/circle.fabric";
import { Rectangle } from "../annotations/objects/rectangle.fabric";
import { Star } from "../annotations/objects/star.fabric";

@Component({
	components: {
		Loading
	}
})
export default class ImageViewer extends Vue {
	@Prop() src!: string;
	@Prop() metadata!: ImageMetaData;

	public loading: boolean = false;
	private canvas!: fabric.Canvas;
	private resizeUnmountFn: Function | null = null;

	$refs!: {
		container: HTMLDivElement;
	};

	mounted() {
		this.setupCanvas();
	}

	destroyed() {
		this.disposeCanvas();
		this.resizeUnmountFn?.();
	}

	@Watch("src")
	@Watch("metadata")
	onChange() {
		this.setupCanvas();
	}

	private setupCanvas() {
		this.disposeCanvas();

		this.canvas = new fabric.Canvas("canvas", { containerClass: "image-viewer-canvas" });

		// Adding image and set size of the canvas based on the image size.
		this.drawImageAndSetCanvasSize();
	}

	public disposeCanvas() {
		// If canvas instance is already exists, we need to dispose that instance to start over
		if (this.canvas) {
			this.canvas.dispose();
			this.canvas = null as any;
		}
	}

	private downloadImageAsBase64Url(url: string, rotate?: number): Promise<string> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = "Anonymous";
			img.onload = () => {
				const canvas = document.createElement("canvas");
				const ctx = canvas.getContext("2d")!;

				const isDimentionChanged = (this.metadata.rotate / 90) % 2 !== 0;
				canvas.width = isDimentionChanged ? img.naturalHeight : img.naturalWidth;
				canvas.height = isDimentionChanged ? img.naturalWidth : img.naturalHeight;

				if (rotate) {
					ctx.translate(canvas.width / 2, canvas.height / 2);
					ctx.rotate((rotate * Math.PI) / 180);
					ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
				} else {
					ctx.drawImage(img, 0, 0);
				}

				const base64Url = canvas.toDataURL();
				resolve(base64Url);
			};
			img.onerror = reject;
			img.src = url;
		});
	}

	private drawImageAndSetCanvasSize() {
		this.loading = true;
		this.downloadImageAsBase64Url(this.src, this.metadata.rotate).then((base64Url) => {
			fabric.Image.fromURL(
				base64Url,
				(img) => {
					const imgWidth = img.getScaledWidth();
					const imgHeight = img.getScaledHeight();

					// If image is cropped, so the width/height of cropper area would get used otherwize image's width/height.
					const width = this.metadata.width || imgWidth;
					const height = this.metadata.height || imgHeight;

					// Set width/height of the canvas
					this.canvas.setDimensions({
						width,
						height
					});

					// Cropper box coordinates
					this.canvas.relativePan(new fabric.Point(-this.metadata.x, -this.metadata.y));

					const imageOptions: Partial<fabric.Image> = {};

					// Image Flip Horizontal
					if (this.metadata.scaleX) {
						imageOptions.scaleX = this.metadata.scaleX;
					}

					// Image Flip Vertical
					if (this.metadata.scaleY) {
						imageOptions.scaleY = this.metadata.scaleY;
					}

					// Setting all options to image that the image editor has.
					img.set(imageOptions);

					this.canvas.setBackgroundImage(img, () => {
						// Calling the function that makes the canvas responsive respective to its container size
						this.resizeUnmountFn?.();
						this.resizeUnmountFn = this.responsiveCanvas(width, height);

						this.canvas.renderAll();
					});

					this.drawAnnotations();

					this.loading = false;
				},
				{ crossOrigin: "Anonymous" }
			);
		});
	}

	private responsiveCanvas(canvasWidth: number, canvasHeight: number): Function {
		const onResize = () => {
			const container = this.$refs.container;

			if (!this.$refs.container) {
				return;
			}

			const containerWidth = container.clientWidth;
			const containerHeight = container.clientHeight;

			const scaleRatio = Math.min(containerWidth / canvasWidth, containerHeight / canvasHeight, 1);
			this.canvas.setDimensions({ width: canvasWidth * scaleRatio, height: canvasHeight * scaleRatio });
			this.canvas.setZoom(scaleRatio);
		};

		const debouncedResize = debounce(onResize, 100);
		window.addEventListener("resize", debouncedResize);
		onResize();

		return () => {
			window.removeEventListener("resize", debouncedResize);
		};
	}

	private drawAnnotations() {
		this.metadata.annotations?.forEach((annotation) => {
			this.addAnnotation(annotation);
		});
	}

	private addAnnotation(shapeProperties: Partial<Annotation>) {
		const shape = shapeProperties as Annotation;
		const options: IObjectOptions = { selectable: false };
		switch (shape.type) {
			case AnnotationTypes.Rect:
				this.canvas.add(new Rectangle(shape, options));
				break;
			case AnnotationTypes.Circle:
				this.canvas.add(new Circle(shape, options));
				break;
			case AnnotationTypes.Star:
				this.canvas.add(new Star(shape, options));
				break;
		}
	}
}
